import React, { useState, useEffect, useCallback, useRef } from "react"
import Typography from "aldoo-ra/Typography"
import Modal from "aldoo-ra/Modal"
import { useRealtime } from "aldoo-ra/Realtime/hooks"
import { showNotification } from "aldoo-ra/Notification"
import { HistoryAPI } from "../history-api"
import HistoryEntryCard from "./HistoryEntryCard"
import HistoryDetailsPanel from "./HistoryDetailsPanel"

const REALTIME_LIMIT = 200

/**
 * Realtime Tab - Shows live history changes via WebSocket
 */
const RealtimeTab = () => {
  const [entries, setEntries] = useState([])
  const [selectedEntry, setSelectedEntry] = useState(null)
  const [loading, setLoading] = useState(false)

  // Establish WebSocket connection
  const { connected: isConnected, socket } = useRealtime('history')

  // Auto-scroll state and refs
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true)
  const scrollRef = useRef(null)
  const isAdjustingScroll = useRef(false)

  // Error handler
  const handleError = useCallback((errorMessage) => {
    showNotification({
      message: errorMessage,
      className: "bg-admin_error text-white",
      duration: 4000
    })
  }, [])

  // Load initial history
  const loadHistory = useCallback(async () => {
    try {
      setLoading(true)

      const response = await HistoryAPI.ListHistory({
        page: 1,
        itemsPerPage: REALTIME_LIMIT
      })

      if (response.result) {
        setEntries(response.data)
        // Enable auto-scroll after initial load
        setShouldAutoScroll(true)

        // Scroll to top after initial load
        requestAnimationFrame(() => {
          if (scrollRef.current) {
            scrollRef.current.scrollTop = 0
          }
        })
      } else {
        handleError(response.error || "Failed to load history")
      }
    } catch (error) {
      handleError(error.message || "Failed to load history")
    } finally {
      setLoading(false)
    }
  }, [handleError])

  // Initial load
  useEffect(() => {
    loadHistory()
  }, [loadHistory])

  // Subscribe to realtime history changes
  useEffect(() => {
    if (!socket || !isConnected) return

    const handleHistoryChange = (data) => {
      // Add new entry to the top of the list
      setEntries(prev => {
        const newEntry = {
          _id: data.id,
          targetModel: data.targetModel,
          targetName: data.targetName,
          action: data.action,
          author: data.author,
          timestamp: data.timestamp,
          changes: data.changes,
          tags: data.tags,
          changelog: data.changelog
        }
        const combined = [newEntry, ...prev]
        // Keep only the latest REALTIME_LIMIT entries
        return combined.slice(0, REALTIME_LIMIT)
      })

      // Auto-scroll to top if enabled
      if (shouldAutoScroll && scrollRef.current) {
        requestAnimationFrame(() => {
          if (scrollRef.current) {
            scrollRef.current.scrollTop = 0
          }
        })
      }
    }

    socket.on('history:change', handleHistoryChange)

    return () => {
      socket.off('history:change', handleHistoryChange)
    }
  }, [socket, isConnected, shouldAutoScroll])

  // Handle scroll to detect manual scrolling
  const handleScroll = useCallback(() => {
    if (isAdjustingScroll.current || !scrollRef.current) return

    const scrollTop = scrollRef.current.scrollTop

    // If user scrolls away from top (scrollTop > 0), disable auto-scroll
    // If user scrolls back to top (scrollTop === 0), enable auto-scroll
    if (scrollTop === 0 && !shouldAutoScroll) {
      setShouldAutoScroll(true)
    } else if (scrollTop > 0 && shouldAutoScroll) {
      setShouldAutoScroll(false)
    }
  }, [shouldAutoScroll])

  // Handle wheel events to detect manual scrolling
  const handleWheel = useCallback((e) => {
    if (isAdjustingScroll.current) return

    // If user is scrolling down (away from top), disable auto-scroll
    if (e.deltaY > 0 && shouldAutoScroll) {
      setShouldAutoScroll(false)
    }
  }, [shouldAutoScroll])

  // Add scroll event listeners
  useEffect(() => {
    const scrollContainer = scrollRef.current
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll)
      scrollContainer.addEventListener('wheel', handleWheel, { passive: true })

      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll)
        scrollContainer.removeEventListener('wheel', handleWheel)
      }
    }
  }, [handleScroll, handleWheel])

  return (
    <div className="h-screen bg-admin_canvas text-admin_text flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 bg-admin_paper border-b border-admin_bordered p-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <Typography 
              component="h1"
              variant="admin_h4"
            >
              Live Changes
            </Typography>

            <Typography 
              variant="admin_body_dim" 
              className="my-0"
            >
              Monitor content changes in real-time as they happen
            </Typography>
          </div>

          {/* Connection Status Indicator */}
          <div 
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-600 ${
              isConnected
                ? 'bg-admin_success/10 border border-admin_success/30'
                : 'bg-admin_error/10 border border-admin_error/30'
            }`}
          >
            <div className={`w-3 h-3 rounded-full transition-all duration-600 ${
              isConnected
                ? 'bg-admin_success animate-pulse'
                : 'bg-admin_error'
            }`} />

            <span className={`text-sm font-medium ${
              isConnected ? 'text-admin_success' : 'text-admin_error'
              }`}
            >
              {isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden p-6">
        <div ref={scrollRef} 
          className="h-full overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper bg-admin_canvas pr-4"
        >
          {loading && entries.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <Typography variant="admin_txt1_dim">
                Loading history...
              </Typography>
            </div>
          ) : entries.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <Typography variant="admin_txt1_dim">
                No history entries found
              </Typography>
            </div>
          ) : (
            <div className="space-y-2">
              {entries.map((entry, idx) => (
                <HistoryEntryCard
                  key={`${entry._id}-${idx}`}
                  entry={entry}
                  isSelected={selectedEntry?._id === entry._id}
                  onClick={() => setSelectedEntry(entry)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {selectedEntry && (
        <Modal
          isOpen={!!selectedEntry}
          onClose={() => setSelectedEntry(null)}
          title="Change Details"
          variant="admin_drawer_right"
          closeOnClickOutside={false}
          passInteractionEvents={true}
          styleOverrides={{
            header: "relative flex justify-between items-center px-6 py-6 border-b border-admin_bordered",
            closeButton: "absolute left-2 top-1/2 -translate-y-1/2 hover:text-admin_text z-10"
          }}
        >
          <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper">
            <HistoryDetailsPanel
              entry={selectedEntry}
              onClose={() => setSelectedEntry(null)}
            />
          </div>
        </Modal>
      )}
    </div>
  )
}

export default RealtimeTab
